import { useCallback } from "react";

import type { BoundaryDraggingPoint } from "../shared/types";
import type { AreaBoundary, PortBoundary, RowBoundary } from "../types/warehouse.types";
import { distance } from "../utils/gpsUtils";

const CLICK_THRESHOLD = 10;

interface UseFindBoundaryPointProps {
  areaBoundaries: AreaBoundary[];
  rowBoundaries: RowBoundary[];
  portBoundaries: PortBoundary[];
  worldToScreen: (x: number, y: number) => { x: number; y: number };
  showAreas: boolean;
  showRows: boolean;
  showPorts: boolean;
}

export const useFindBoundaryPoint = ({
  areaBoundaries,
  rowBoundaries,
  portBoundaries,
  worldToScreen,
  showAreas,
  showRows,
  showPorts,
}: UseFindBoundaryPointProps) => {
  const findBoundaryPoint = useCallback(
    (screenX: number, screenY: number): BoundaryDraggingPoint | null => {
      const checkDistance = (
        worldCoord: { x: number; y: number },
        point: BoundaryDraggingPoint
      ): BoundaryDraggingPoint | null => {
        const pos = worldToScreen(worldCoord.x, worldCoord.y);
        return distance(screenX, screenY, pos.x, pos.y) < CLICK_THRESHOLD ? point : null;
      };

      // Check ports first (if enabled)
      if (showPorts) {
        for (let bIdx = 0; bIdx < portBoundaries.length; bIdx++) {
          const boundary = portBoundaries[bIdx];
          for (let pIdx = 0; pIdx < boundary.polygon.length; pIdx++) {
            const found = checkDistance(boundary.polygon[pIdx], {
              boundaryIdx: bIdx,
              pointIdx: pIdx,
              type: "port",
            });
            if (found) return found;
          }
        }
      }

      // Check rows (if enabled)
      if (showRows) {
        for (let bIdx = 0; bIdx < rowBoundaries.length; bIdx++) {
          const boundary = rowBoundaries[bIdx];
          for (let pIdx = 0; pIdx < boundary.polygon.length; pIdx++) {
            const found = checkDistance(boundary.polygon[pIdx], {
              boundaryIdx: bIdx,
              pointIdx: pIdx,
              type: "row",
            });
            if (found) return found;
          }
        }
      }

      // Check areas (if enabled)
      if (showAreas) {
        for (let bIdx = 0; bIdx < areaBoundaries.length; bIdx++) {
          const boundary = areaBoundaries[bIdx];
          for (let pIdx = 0; pIdx < boundary.polygon.length; pIdx++) {
            const found = checkDistance(boundary.polygon[pIdx], {
              boundaryIdx: bIdx,
              pointIdx: pIdx,
              type: "area",
            });
            if (found) return found;
          }
        }
      }

      return null;
    },
    [areaBoundaries, rowBoundaries, portBoundaries, worldToScreen, showAreas, showRows, showPorts]
  );

  return { findBoundaryPoint };
};
